import { useCallback, useMemo } from 'react';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { useLazyGetWarrantyAuditPdfDataQuery } from '../../../../../api/endpoints/warrantyAudit';
import PdfGenerationService from '../../../../../utils/pdfGenerationService';
import toastService from '../../../../../utils/toastService';

const useWarrantyAuditPdf = () => {
    const { th, getCurrentLanguageId } = useLanguage();
    const languageId = getCurrentLanguageId();
    const pdfService = useMemo(() => new PdfGenerationService(), []);
    const [fetchPdfData, { isLoading, isFetching }] = useLazyGetWarrantyAuditPdfDataQuery();

    const buildTranslations = useCallback(
        () => ({
            title: th('warrantyAudit.pdf.title') || 'Denetim Sonuçları',
            auditNo: th('warrantyAudit.pdf.auditNo') || 'Denetim Nr',
            auditDate: th('warrantyAudit.pdf.auditDate') || 'Denetim Tarihi',
            dealer: th('warrantyAudit.pdf.dealer') || 'Bayi/Servis',
            auditor: th('warrantyAudit.pdf.auditor') || 'Denetçi',
            totalScore: th('warrantyAudit.pdf.totalScore') || 'Toplam Puan',
            averageScore: th('warrantyAudit.pdf.averageScore') || 'Ortalama Puan',
            result: th('warrantyAudit.pdf.result') || 'Sonuç',
            criterion: th('warrantyAudit.fields.criterion') || 'Kriter',
            sort: th('warrantyAudit.fields.sort') || 'Sıralama',
            subCriterion: th('warrantyAudit.fields.subCriterion') || 'Alt Kriter',
            criterionText: th('warrantyAudit.evaluation.criterionText') || 'Değerlendirme Kriter',
            evaluationText: th('warrantyAudit.evaluation.evaluationText') || 'Değerlendirme',
            point: th('warrantyAudit.fields.point') || 'Puan',
            score: th('warrantyAudit.fields.auditScore') || 'Denetim',
            maxScore: th('warrantyAudit.fields.maxScore') || 'Maks. Puan',
            sectionScore: th('warrantyAudit.fields.sectionScore') || 'Bölüm Puanı',
            scoreBands: th('warrantyAudit.pdf.scoreBands') || 'Puan Aralıkları'
        }),
        [th]
    );

    const generate = useCallback(
        async (id, { download = false } = {}) => {
            if (!id) return;
            try {
                const data = await fetchPdfData({ id, languageId }).unwrap();
                if (!data) {
                    toastService.error(th('warrantyAudit.toast.pdfError') || 'PDF verisi alınamadı');
                    return;
                }
                const pdfDoc = pdfService.generateWarrantyAuditPdf(data, buildTranslations());
                if (download) {
                    pdfService.downloadPdf(pdfDoc, `WarrantyAudit_${id}.pdf`);
                } else {
                    pdfService.viewPdf(pdfDoc);
                }
            } catch (error) {
                toastService.error(th('warrantyAudit.toast.pdfError') || 'PDF oluşturulamadı');
            }
        },
        [fetchPdfData, languageId, pdfService, buildTranslations, th]
    );

    return { generate, isGenerating: isLoading || isFetching };
};

export default useWarrantyAuditPdf;
